import { createContext, useContext, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { usePublicAuth } from './PublicAuthContext';
import { aurasApi } from '../lib/api';

// Default fallback presets (used while loading from API)
const DEFAULT_AURA_PRESETS = {
  monochrome: {
    name: 'Monocromático',
    colors: ['#FFFFFF', '#E0E0E0', '#BDBDBD', '#9E9E9E', '#FFFFFF', '#F5F5F5'],
    premium: false,
  },
  off: {
    name: 'Desactivado',
    colors: null,
    premium: false,
  },
  siri: {
    name: 'Siri',
    colors: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96E6A1', '#DDA0DD', '#FFB347'],
    premium: true,
  },
  fire: {
    name: 'Fuego',
    colors: ['#FF4500', '#FF6347', '#FFD700', '#FF8C00', '#DC143C', '#FF1493'],
    premium: true,
  },
  ocean: {
    name: 'Océano',
    colors: ['#00CED1', '#20B2AA', '#48D1CC', '#40E0D0', '#00BFFF', '#1E90FF'],
    premium: true,
  },
  aurora: {
    name: 'Aurora',
    colors: ['#00FF87', '#60EFFF', '#FF00E5', '#7B68EE', '#00CED1', '#FF6B6B'],
    premium: true,
  },
  sunset: {
    name: 'Atardecer',
    colors: ['#FF6B6B', '#FFA07A', '#FFD700', '#FF4500', '#FF69B4', '#DDA0DD'],
    premium: true,
  },
  neon: {
    name: 'Neón',
    colors: ['#FF00FF', '#00FFFF', '#FF00FF', '#FFFF00', '#00FF00', '#FF0080'],
    premium: true,
  },
};

const AuraContext = createContext(null);

// Cursor glow component - SMALLER size as requested
const CursorGlow = ({ colors }) => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);
  
  if (!colors) return null;
  
  // Reduced size: was 80px (w-20), now 32px (w-8)
  return (
    <motion.div
      className="fixed pointer-events-none"
      style={{ zIndex: 99999 }}
      animate={{
        x: mousePos.x - 16,
        y: mousePos.y - 16,
      }}
      transition={{ type: "spring", damping: 30, stiffness: 400 }}
    >
      <div 
        className="w-8 h-8 rounded-full blur-md opacity-50"
        style={{
          background: `radial-gradient(circle, ${colors[0]} 0%, ${colors[2]} 50%, transparent 70%)`,
        }}
      />
    </motion.div>
  );
};

// Global aura effects component - renders via portal to ensure visibility across all pages
const GlobalAuraEffects = ({ colors, applyToPage }) => {
  const [mounted, setMounted] = useState(false);
  
  useEffect(() => {
    setMounted(true);
  }, []);
  
  if (!applyToPage || !colors || !mounted) return null;
  
  // Use portal to render effects at the root level, outside React's tree
  return createPortal(
    <div id="global-aura-effects" style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9998 }}>
      {/* Cursor glow */}
      <CursorGlow colors={colors} />
      
      {/* Top ambient glow */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-96 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at top, ${colors[0]}15 0%, transparent 50%)`,
          zIndex: 1,
        }}
        animate={{ opacity: [0.5, 0.8, 0.5] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />
      
      {/* Left side accent */}
      <motion.div
        className="fixed top-1/4 -left-32 w-64 h-64 rounded-full blur-3xl pointer-events-none"
        style={{ background: colors[1], zIndex: 1 }}
        animate={{ 
          opacity: [0.05, 0.1, 0.05],
          y: [0, 50, 0],
        }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      
      {/* Right side accent */}
      <motion.div
        className="fixed bottom-1/4 -right-32 w-64 h-64 rounded-full blur-3xl pointer-events-none"
        style={{ background: colors[2], zIndex: 1 }}
        animate={{ 
          opacity: [0.05, 0.1, 0.05],
          y: [0, -50, 0],
        }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />

      {/* Floating particles */}
      {colors.slice(0, 3).map((color, i) => (
        <motion.div
          key={i}
          className="fixed w-1.5 h-1.5 rounded-full pointer-events-none"
          style={{ 
            background: color,
            boxShadow: `0 0 15px ${color}`,
            left: `${20 + i * 30}%`,
            top: '20%',
            zIndex: 1,
          }}
          animate={{
            y: [0, 500, 0],
            x: [0, i % 2 === 0 ? 100 : -100, 0],
            opacity: [0, 0.6, 0],
          }}
          transition={{
            duration: 8 + i * 2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 2,
          }}
        />
      ))}
    </div>,
    document.body
  );
};

export const AuraProvider = ({ children }) => {
  const { user, isAuthenticated } = usePublicAuth();
  const [auraPreset, setAuraPreset] = useState('monochrome');
  const [applyToPage, setApplyToPage] = useState(false);
  const [auraPresets, setAuraPresets] = useState(DEFAULT_AURA_PRESETS);
  const [presetsLoaded, setPresetsLoaded] = useState(false);
  
  // Load aura presets from API
  useEffect(() => {
    const loadPresets = async () => {
      try {
        const response = await aurasApi.getPresets(false);
        const presets = {};
        response.data.forEach(preset => {
          presets[preset.id] = {
            name: preset.name,
            colors: preset.colors?.length > 0 ? preset.colors : null,
            premium: preset.premium,
          };
        });
        setAuraPresets(presets);
        setPresetsLoaded(true);
      } catch (error) {
        console.error('Error loading aura presets:', error);
        // Keep using defaults on error
      }
    };
    loadPresets();
  }, []);
  
  // Load user preferences when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      setAuraPreset(user.aura_preset || 'monochrome');
      setApplyToPage(user.aura_apply_to_page || false);
    } else {
      // Reset when logged out
      setAuraPreset('monochrome');
      setApplyToPage(false);
    }
  }, [isAuthenticated, user]);
  
  const currentColors = auraPresets[auraPreset]?.colors || null;
  
  return (
    <AuraContext.Provider value={{
      auraPreset,
      setAuraPreset,
      applyToPage,
      setApplyToPage,
      currentColors,
      AURA_PRESETS: auraPresets,
      presetsLoaded,
    }}>
      {/* Global aura effects rendered at app level */}
      <GlobalAuraEffects colors={currentColors} applyToPage={applyToPage} />
      {children}
    </AuraContext.Provider>
  );
};

export const useAura = () => {
  const context = useContext(AuraContext);
  if (!context) {
    throw new Error('useAura must be used within an AuraProvider');
  }
  return context;
};

// Export default presets for fallback
export { DEFAULT_AURA_PRESETS as AURA_PRESETS };
